import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import type { ManagerOptions, SocketOptions } from 'socket.io-client';

import { environment } from '../../../environments/environment';
import { RealtimeTokenService } from './realtime-token.service';
import {
  RECONNECT_MAX_ATTEMPTS,
  isHandshakeRejection,
  reconnectDelayMs,
} from './realtime-reconnect-policy';

export type SocketIoOptions = Partial<ManagerOptions & SocketOptions>;
export type SocketIoFactory = (uri: string, opts?: SocketIoOptions) => Socket;

/** The `io()` factory of socket.io-client, injectable so tests can hand in a fake socket. */
export const SOCKET_IO_FACTORY = new InjectionToken<SocketIoFactory>('SOCKET_IO_FACTORY', {
  providedIn: 'root',
  factory: () => io,
});

/**
 * How the live socket authenticated.
 * - `channel`: channel token minted by kredit, sent in `auth.token`, WebSocket only (socket v2, R2).
 * - `legacy`: kredit JWT in the `x-token` header, default transports. Rollout fallback, removed in R4.
 */
export type RealtimeConnectionMode = 'channel' | 'legacy';

interface ConnectCredential {
  mode: RealtimeConnectionMode;
  url: string;
  token: string;
}

type CredentialResolution =
  | { outcome: 'ready'; credential: ConnectCredential }
  | { outcome: 'retry'; reason: string }
  | { outcome: 'stop'; reason: string };

/** Storage key of the console JWT: written by AuthService (`tokenName`), renewed in place by the interceptor. */
const CONSOLE_JWT_STORAGE_KEY = 'x-token';

const LOG = '[realtime]';


@Injectable({
  providedIn: 'root'
})
export class SocketioService {

  private usersActives$: BehaviorSubject<any> = new BehaviorSubject([]);
  public usersMsg$: BehaviorSubject<any> = new BehaviorSubject([]);
  private resetstadistic$: BehaviorSubject<any> = new BehaviorSubject(null);
  private resetstadistic_approvated$: BehaviorSubject<any> = new BehaviorSubject(null);
  private resetDataClientCode$: BehaviorSubject<any> = new BehaviorSubject(null);
  private changes_direct_debit$: BehaviorSubject<any> = new BehaviorSubject(null);

  /** The live socket, or null while there is none (AuthService.setToken reads `.connected`). */
  socket: Socket | null = null;
  private connet = false;

  private connectionMode: RealtimeConnectionMode | null = null;
  /** JWT handed in by AuthService.setToken; only the legacy fallback sends it to the socket. */
  private consoleJwt: string | null = null;
  /** Consecutive failed attempts since the last successful connect. */
  private attempt = 0;
  /** Bumped on every teardown so a token request resolving late cannot open a stale socket. */
  private generation = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private legacyFallbackWarned = false;

  constructor(
    private readonly tokenService: RealtimeTokenService,
    @Inject(SOCKET_IO_FACTORY) private readonly createSocket: SocketIoFactory,
  ) {}


  get usersActives() {
    return this.usersActives$;
  }

  get resetstadistic() {
    return this.resetstadistic$;
  }

  get resetstadistic_approvated() {
    return this.resetstadistic_approvated$;
  }

  get changesDirectDebit() {
    return this.changes_direct_debit$;
  }

  get resetDataClientCode() {
    return this.resetDataClientCode$;
  }

  /** `channel`, `legacy`, or null while no socket has been opened. */
  get mode(): RealtimeConnectionMode | null {
    return this.connectionMode;
  }

  get statusConnet() {
    return this.connet;
  }

  get socketConnet() {
    return this.socket;
  }

  /**
   * Opens the realtime channel for the session that owns `consoleJwt`
   * (called by AuthService.setToken). Any previous socket, pending retry or
   * in-flight token request is dropped first: there is never more than one
   * live socket.
   */
  setupSocketConnection(consoleJwt: string): void {
    this.consoleJwt = consoleJwt;
    this.teardown();
    this.attempt = 0;
    void this.connect();
  }

  /** Closes the channel and cancels any pending reconnection (logout). */
  disconnect(): void {
    this.teardown();
    this.attempt = 0;
  }

  mensajePrivado(uid: any, mensaje: any = ''): void {
    this.socket?.emit('mensaje-privado', { uid, mensaje });
  }

  setUsersMsg(payload: any): void {
    const msgs = [...this.usersMsg$.getValue(), payload];
    this.usersMsg$.next(msgs);
  }

  private async connect(): Promise<void> {
    const generation = this.generation;
    const resolution = await this.resolveCredential();
    if (generation !== this.generation) {
      return; // superseded by setupSocketConnection/disconnect while the token was in flight
    }
    switch (resolution.outcome) {
      case 'ready':
        this.openSocket(resolution.credential);
        return;
      case 'retry':
        this.scheduleRetry(resolution.reason);
        return;
      case 'stop':
        console.error(`${LOG} not connecting: ${resolution.reason}`);
        return;
    }
  }

  /**
   * Primary path: a channel token from `GET /api/realtime/token`, whose `url`
   * is where the console connects (the environment's socket URL is only a
   * fallback). Rollout compatibility, removed in R4: a 404 means kredit does
   * not serve channel tokens yet, so the console falls back to the kredit JWT
   * in `x-token`. 503 (kredit without the secret) and network errors are
   * retried with backoff; 401/403 mean this session cannot use the channel.
   */
  private async resolveCredential(): Promise<CredentialResolution> {
    try {
      const minted = await this.tokenService.mint();
      const token = typeof minted?.token === 'string' ? minted.token.trim() : '';
      if (token === '') {
        return { outcome: 'retry', reason: 'token endpoint answered without a token' };
      }
      const url = minted.url || this.environmentSocketUrl();
      if (!url) {
        return { outcome: 'stop', reason: 'no socket url: the token endpoint returned none and the environment has no socketBase' };
      }
      return { outcome: 'ready', credential: { mode: 'channel', url, token } };
    } catch (error) {
      const status = error instanceof HttpErrorResponse ? error.status : null;
      if (status === 404) {
        return this.legacyCredential();
      }
      if (status === 401 || status === 403) {
        return { outcome: 'stop', reason: `token endpoint answered ${status}: this session is not allowed on the realtime channel` };
      }
      return { outcome: 'retry', reason: describeMintError(error) };
    }
  }

  private legacyCredential(): CredentialResolution {
    if (!this.legacyFallbackWarned) {
      this.legacyFallbackWarned = true;
      console.warn(`${LOG} GET /api/realtime/token answered 404 (kredit without channel tokens yet): falling back to the kredit JWT in x-token. Remove this fallback in R4.`);
    }
    const url = this.environmentSocketUrl();
    const token = this.currentConsoleJwt();
    if (!url || !token) {
      return { outcome: 'stop', reason: 'legacy mode needs environment.socketBase.SOCKET_ENDPOINT and a console JWT' };
    }
    return { outcome: 'ready', credential: { mode: 'legacy', url, token } };
  }

  /** The freshest console JWT: the interceptor renews it in storage (sliding token) without going through setToken. */
  private currentConsoleJwt(): string | null {
    try {
      return localStorage.getItem(CONSOLE_JWT_STORAGE_KEY) ?? this.consoleJwt;
    } catch {
      return this.consoleJwt;
    }
  }

  /** Socket URL of the build (`environment.socketBase.SOCKET_ENDPOINT`): primary before R2, fallback now. */
  private environmentSocketUrl(): string | null {
    return environment?.socketBase?.SOCKET_ENDPOINT || null;
  }

  private openSocket(credential: ConnectCredential): void {
    // `forceNew`: io() caches one Manager per URL; a fresh one per attempt
    // guarantees the credential of this attempt is the one in the handshake.
    const options: SocketIoOptions = credential.mode === 'channel'
      ? { auth: { token: credential.token }, transports: ['websocket'], reconnection: true, forceNew: true }
      : { extraHeaders: { 'x-token': credential.token }, reconnection: true, forceNew: true };
    this.connectionMode = credential.mode;
    const socket = this.createSocket(credential.url, options);
    this.socket = socket;
    this.bindLifecycleEvents(socket);
    this.bindConsoleEvents(socket);
  }

  private bindLifecycleEvents(socket: Socket): void {
    socket.on('connect', () => {
      this.connet = true;
      this.attempt = 0;
      console.log(`${LOG} connected (${this.connectionMode})`);
    });

    // Transport closes ('transport close', 'ping timeout') are retried by
    // socket.io itself with the same credential. 'io server disconnect' is
    // the server closing this session (revocation): nobody retries that.
    socket.on('disconnect', (reason: string) => {
      this.markDisconnected();
      console.log(`${LOG} disconnected: ${reason}`);
    });

    // A handshake rejected by the server middleware ('expired', 'revoked',
    // 'unmapped_role', 'rate_limited', ...) is final for this credential.
    // socket.io-client 3.1 would keep the engine open and resend the same
    // credential every ~45 s (the server's connectTimeout); instead, close it
    // and come back with a fresh token after a backoff.
    socket.on('connect_error', (error: Error) => {
      if (!isHandshakeRejection(error)) {
        console.warn(`${LOG} transport error, socket.io retries on its own: ${error?.message}`);
        return;
      }
      console.warn(`${LOG} handshake rejected by the server: ${error.message}`);
      this.closeSocket();
      this.scheduleRetry(error.message);
    });
  }

  private bindConsoleEvents(socket: Socket): void {
    socket.on('users-activos', (payload: any) => {
      this.usersActives$.next(payload);
    });

    socket.on('reset-stadistic', (resetstadistic: any) => {
      this.resetstadistic$.next(resetstadistic);
    });

    socket.on('reset-stadistic-approvated', () => {
      console.log('reset-stadistic-approvated');
      // this.resetstadistic_approvated$.next(...) stays disabled, as before.
    });

    socket.on('changesDirectDebit', (cdd: any) => {
      console.log('changesDirectDebit', cdd);
      this.changes_direct_debit$.next(cdd);
    });

    socket.on('reset-Data-client-code', (resetDataClientCode: any) => {
      this.resetDataClientCode$.next(resetDataClientCode);
    });

    socket.on('escuchar-mensaje-privado', (payload: any) => {
      console.log('para mi ', payload);
      this.setUsersMsg(payload);
    });
  }

  private scheduleRetry(reason: string): void {
    this.attempt += 1;
    if (this.attempt > RECONNECT_MAX_ATTEMPTS) {
      console.error(`${LOG} giving up after ${RECONNECT_MAX_ATTEMPTS} attempts (last reason: ${reason}); the next login reopens the channel`);
      return;
    }
    const delayMs = reconnectDelayMs(this.attempt);
    console.warn(`${LOG} reconnecting in ${delayMs} ms (attempt ${this.attempt}/${RECONNECT_MAX_ATTEMPTS}): ${reason}`);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.connect();
    }, delayMs);
  }

  private teardown(): void {
    this.generation += 1;
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.closeSocket();
  }

  private closeSocket(): void {
    const socket = this.socket;
    if (!socket) {
      return;
    }
    this.socket = null;
    socket.removeAllListeners(); // stale callbacks must not touch the next socket's state
    socket.disconnect();         // closes its Manager too: this socket is the only namespace
    this.markDisconnected();
  }

  private markDisconnected(): void {
    this.connet = false;
    this.usersActives$.next([]);
  }
}

function describeMintError(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    return error.status === 0
      ? 'token endpoint unreachable (network error)'
      : `token endpoint answered ${error.status}`;
  }
  return error instanceof Error ? error.message : String(error);
}
